import { useState, useRef } from "react";
import { Loader } from "shared/components/Loader";
import { Show } from "shared/components";
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { DataUrlItem, Step } from "./types";
import { CheckoutSlice } from "./slices/Checkout/Checkout";
import { findDataUrls, set, uploadLinks, validateHousing } from "./helpers";
import { AX_ORIGIN } from "./constants";
import { Aside } from "./components/Aside";
import { IframeLayout } from "./components/IframeLayout";
import { toast } from "react-toastify";
import {
  ApplicantStatus,
  Application,
  Verification,
  VerificationNames
} from "shared/types";
import { StartApplicationSplash } from "./slices/StartApplicationSplash";
import { getErrorMessage } from "utils/error";
import {
  useApplicant,
  useCurrentSteps,
  useGoToFirstUnfinishedStep,
  useIframeMessaging
} from "./hooks";
import { Configuration } from "./slices/Configuration";
import { MoveInDateSlice } from "./slices/MoveInDate";

const ManualApplicationFlow = ({
  setOpen,
  applicationId,
  applicant,
  verificationsMeta,
  desiredMoveInDate,
  refetchApplication
}: {
  setOpen: (open: boolean) => void;
  applicationId: number;
  applicant: Application["applicants"][number];
  desiredMoveInDate?: string;
  verificationsMeta: UseQueryResult<
    {
      verifications: Verification[];
    },
    Error
  >;
  refetchApplication: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<Application, Error>>;
}) => {
  const applicantId = applicant.id;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const queryClient = useQueryClient();
  // Iframe loading state
  const [loading, setLoading] = useState(true);
  // Stepper
  const [activeStepName, setActiveStepName] = useState<string | null>(null);

  // If user finished self reported income and SELECTED YES
  const hasIncomeSource =
    (
      applicant.verifications
        ?.find(
          (verification) =>
            verification.name === VerificationNames.IncomeVerification
        )
        ?.steps?.find((step) => step.name === "income_verification") as
        | Step
        | undefined
    )?.data.do_you_have_source_of_income === "Yes";

  const { allSteps, stepNames } = useCurrentSteps({
    applicationId,
    applicantId,
    metaVerificationsData: verificationsMeta.data?.verifications,
    metaVerificationsIsFetching: verificationsMeta.isFetching,
    hasIncomeSource,
    activeStepName,
    configuration: {
      preferredCheckoutType: applicant.preferred_checkout_type,
      preferredSubmissionType: applicant.preferred_submission_disclosure_type
    }
  });

  // TODO: add config here
  useGoToFirstUnfinishedStep({
    allSteps,
    activeStepName,
    setActiveStepName,
    applicant,
    moveInDate: desiredMoveInDate
  });

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const verificationId = allSteps[activeStepName || ""]?.id;

  const updateStepMeta = useMutation({
    mutationKey: [
      "updateStepMeta",
      applicationId,
      applicantId,
      activeStepName,
      verificationId
    ],
    mutationFn: (data: any) =>
      instance.patch(
        `/portal/v1/applications/${applicationId}/verifications/${verificationId}/steps/${activeStepName}?applicant_id=${applicantId}`,
        { data }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", String(applicationId)]
      });
      verificationsMeta.refetch();
    }
  });

  useIframeMessaging({
    iframeRef,
    activeStepName,
    allSteps,
    onSubmit: async (formData) => {
      try {
        const isHousingStep = activeStepName === "address";
        if (isHousingStep) {
          // Check if more than 2 years of housing history
          const ret = validateHousing(formData);
          if (ret) {
            if (ret.errors.length) {
              toast.error(ret.errors[0].message);
              return;
            }
            if (!ret.hasFullTwoYears) {
              toast.error("2 years of housing history is required");
              return;
            }
          }
        }

        const dataUrlFiles: DataUrlItem[] = [];
        findDataUrls(formData, "", dataUrlFiles);

        const links = dataUrlFiles.length
          ? await uploadLinks(dataUrlFiles, String(applicantId))
          : [];

        const patchedData = links.reduce(
          (data, [path, gid]) => set(data, path, gid),
          { ...formData }
        );

        await updateStepMeta.mutateAsync(patchedData);

        if (!activeStepName) return;

        const currentIndex = stepNames.indexOf(activeStepName);
        let nextStepName: string | undefined;

        const isIncomeStep = activeStepName === "income_verification";
        if (isIncomeStep) {
          await verificationsMeta.refetch();
          await refetchApplication();

          const saidNoIncome =
            patchedData.do_you_have_source_of_income === "No";

          const hasVerifiedIncome = verificationsMeta.data?.verifications.some(
            (verification) =>
              verification.name === VerificationNames.VerifiedIncomeVerification
          );

          if (saidNoIncome)
            if (applicant.preferred_checkout_type === "send_by_email") {
              /* empty -- falls back to close modal */
            } else nextStepName = "checkout";
          else if (hasVerifiedIncome) nextStepName = "income_documents";
          else nextStepName = stepNames[currentIndex + 1];
        } else nextStepName = stepNames[currentIndex + 1];

        if (nextStepName) setActiveStepName(nextStepName);
        else setOpen(false);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    setLoading
  });

  if (
    // !allSteps ||
    !stepNames.length ||
    !activeStepName
  )
    return null;

  return (
    <div className="flex divide-x overflow-hidden">
      <Aside
        applicant={applicant}
        stepNames={stepNames}
        allSteps={allSteps}
        isSubmissionSentByEmail={
          applicant.preferred_submission_disclosure_type === "send_by_email"
        }
        activeStepName={activeStepName}
        desiredMoveInDate={desiredMoveInDate}
      />
      <IframeLayout
        goBack={
          activeStepName === "checkout"
            ? () => {
                const prevIdx = stepNames.length - 2;
                if (!prevIdx) return;

                if (stepNames[prevIdx] === "verified_income") {
                  // check if income_verification is not no
                  const incomeVerificationStep =
                    allSteps["income_verification"];
                  const incomeVerificationData = incomeVerificationStep.data;
                  const hasIncomeSource =
                    incomeVerificationData.do_you_have_source_of_income ===
                    "Yes";
                  if (!hasIncomeSource) setActiveStepName(stepNames[prevIdx]);
                  else setActiveStepName(stepNames[prevIdx - 1]);
                } else if (prevIdx >= 0) setActiveStepName(stepNames[prevIdx]);
              }
            : undefined
        }
        activeStepName={activeStepName}
        setOpen={setOpen}
        setActiveStepName={setActiveStepName}
        stepNames={stepNames}
      >
        <div className="relative">
          {activeStepName === "move_in_date" ? (
            <MoveInDateSlice
              onSuccess={() => {
                const currentIdx = stepNames.indexOf("move_in_date");
                const nextIdx = currentIdx + 1;

                refetchApplication().then(() => {
                  if (nextIdx >= 0) setActiveStepName(stepNames[nextIdx]);
                });
              }}
              applicationId={Number(applicationId)}
              moveInDate={desiredMoveInDate}
            />
          ) : activeStepName === "checkout" ? (
            <CheckoutSlice
              onSuccess={() => {
                setOpen(false);
              }}
              goBack={() => {
                // Check if verified_income is not needed

                const currentIdx = stepNames.indexOf(activeStepName!);
                const nextIdx = currentIdx - 1;

                if (stepNames[nextIdx] === "verified_income") {
                  // check if income_verification is not no
                  const incomeVerificationStep =
                    allSteps["income_verification"];
                  const incomeVerificationData = incomeVerificationStep.data;
                  const hasIncomeSource =
                    incomeVerificationData.do_you_have_source_of_income ===
                    "Yes";
                  if (!hasIncomeSource) setActiveStepName(stepNames[nextIdx]);
                  else setActiveStepName(stepNames[nextIdx - 1]);
                } else if (nextIdx >= 0) setActiveStepName(stepNames[nextIdx]);
              }}
              applicationFee={applicant.application_fee}
              steps={allSteps}
              setActiveStepName={setActiveStepName}
              ineligibleToCheckout={applicant.ineligible_to_checkout_reason}
              applicationId={Number(applicationId)}
              applicantId={Number(applicantId)}
              hasIncomeSource={hasIncomeSource}
              desiredMoveInDate={desiredMoveInDate}
              sendDisclosureEmail={
                applicant.preferred_submission_disclosure_type ===
                "send_by_email"
              }
              isSubmissionDisclosureFinished={
                verificationsMeta.data?.verifications.some(
                  (verification) =>
                    verification.name ===
                      VerificationNames.SubmissionDisclosure &&
                    verification.status === "finished"
                ) || false
              }
            />
          ) : (
            <iframe
              ref={iframeRef}
              key={activeStepName}
              onLoad={() => setLoading(false)}
              src={`${AX_ORIGIN}/manual-applications`}
              className="w-full h-[calc(80vh-72px)] overflow-hidden animate-appear bg-background-900 rounded-br-lg"
            />
          )}
          <Show
            if={
              loading &&
              activeStepName !== "checkout" &&
              activeStepName !== "configuration" &&
              activeStepName !== "move_in_date"
            }
          >
            <div className="absolute inset-0 flex items-center justify-center animate-appear">
              <Loader />
            </div>
          </Show>
        </div>
      </IframeLayout>
    </div>
  );
};

export const ManualApplication = ({
  setOpen,
  applicationId,
  applicantId
}: {
  setOpen: (open: boolean) => void;
  applicationId?: number;
  applicantId?: number;
}) => {
  const verificationsMeta = useQuery({
    queryKey: ["verificationsMeta", String(applicationId), String(applicantId)],
    queryFn: () =>
      instance
        .get<{
          verifications: Verification[];
        }>(
          `/portal/v1/applications/${applicationId}/verifications?applicant_id=${applicantId}`
        )
        .then((res) => {
          return res.data;
        }),
    enabled: Boolean(applicationId) && Boolean(applicantId)
  });

  const { applicant, isApplicationLoading, refetchApplication, application } =
    useApplicant({
      applicationId,
      applicantId
    });

  if (isApplicationLoading)
    return (
      <div className="h-full flex items-center justify-center animate-appear">
        <Loader />
      </div>
    );

  if (applicant?.application_status === ApplicantStatus.Pending)
    return (
      <StartApplicationSplash
        applicantFullName={`${applicant.first_name} ${applicant.last_name}`}
        applicantId={Number(applicantId)}
        applicationId={Number(applicationId)}
        onSuccess={() => {
          refetchApplication();
          verificationsMeta.refetch();
        }}
        onClose={() => setOpen(false)}
      />
    );

  if (!applicant || !applicationId) return null;

  if (
    !applicant.preferred_checkout_type ||
    !applicant.preferred_submission_disclosure_type
  )
    return (
      <IframeLayout
        activeStepName="configuration"
        setOpen={setOpen}
        setActiveStepName={() => {}}
        stepNames={[]}
        noBack
      >
        <Configuration
          isFreeCheckout={!Boolean(applicant.application_fee?.cents)}
          onSuccess={() => {
            refetchApplication();
          }}
          applicantId={Number(applicantId)}
          applicationId={Number(applicationId)}
          preferredCheckoutType={applicant.preferred_checkout_type}
          preferredSubmissionType={
            applicant.preferred_submission_disclosure_type
          }
        />
      </IframeLayout>
    );

  return (
    <ManualApplicationFlow
      desiredMoveInDate={application?.desired_move_in_date}
      refetchApplication={refetchApplication}
      setOpen={setOpen}
      applicationId={applicationId}
      applicant={applicant}
      verificationsMeta={verificationsMeta}
    />
  );
};
